import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { apiClient } from "@/App";
import { toast } from "sonner";
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Building2,
  Edit,
  Trash2,
  Plus,
  FolderKanban,
  IndianRupee,
  FileText,
  Send,
  Upload,
  Calculator,
  Download,
  UserPlus,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { EditClientModal, CreateProjectModal, EmailConfirmationModal } from "@/components/client/ClientModals";

export default function ClientDetail() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(false);
  
  const [formData, setFormData] = useState({});
  const [projectForm, setProjectForm] = useState({
    name: "",
    project_type: "architecture",
    site_location: "",
    approximate_sqft: "",
    rate_per_sqft: "",
    discount_amount: "0",
    final_agreed_amount: "",
    gst_percentage: "18",
    advance_amount: "40000",
    site_visits_included: "10"
  });
  
  const [emailForm, setEmailForm] = useState({
    subject: "Scope of Work - ",
    custom_message: "",
    send_email: false,
    attachment: null
  });
  
  // Additional email form for editing
  const [newAdditionalEmail, setNewAdditionalEmail] = useState({ name: "", email: "", role: "" });

  useEffect(() => {
    fetchClient();
    fetchClientProjects();
    checkEmailStatus();
  }, [clientId]);

  const fetchClient = async () => {
    try {
      const response = await apiClient.get(`/clients/${clientId}`);
      setClient(response.data);
      setFormData(response.data);
    } catch (error) {
      console.error("Failed to fetch client:", error);
      toast.error("Failed to load client details");
      navigate("/clients");
    } finally {
      setLoading(false);
    }
  };

  const fetchClientProjects = async () => {
    try {
      const response = await apiClient.get(`/projects?client_id=${clientId}`);
      setProjects(response.data);
    } catch (error) {
      console.error("Failed to fetch projects:", error);
    }
  };

  const checkEmailStatus = async () => {
    try {
      const response = await apiClient.get("/email/status");
      setEmailConfigured(response.data.configured);
    } catch (error) {
      console.error("Failed to check email status:", error);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await apiClient.put(`/clients/${clientId}`, formData);
      toast.success("Client updated successfully");
      setShowEditModal(false);
      fetchClient();
    } catch (error) {
      toast.error("Failed to update client");
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await apiClient.put(`/clients/${clientId}`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      fetchClient();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const calculateAmounts = () => {
    const sqft = parseFloat(projectForm.approximate_sqft) || 0;
    const rate = parseFloat(projectForm.rate_per_sqft) || 0;
    const discount = parseFloat(projectForm.discount_amount) || 0;
    const gst = parseFloat(projectForm.gst_percentage) || 18;
    
    const quoted = sqft * rate;
    const final = quoted - discount;
    const gstAmount = final * (gst / 100);
    const total = final + gstAmount;
    
    return { quoted, final, gstAmount, total };
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const amounts = calculateAmounts();
      const data = {
        client_id: clientId,
        name: projectForm.name || client.project_name,
        project_type: projectForm.project_type || client.project_type,
        site_location: projectForm.site_location || client.site_location,
        approximate_sqft: parseFloat(projectForm.approximate_sqft),
        rate_per_sqft: parseFloat(projectForm.rate_per_sqft),
        discount_amount: parseFloat(projectForm.discount_amount) || 0,
        final_agreed_amount: projectForm.final_agreed_amount ? parseFloat(projectForm.final_agreed_amount) : amounts.final,
        gst_percentage: parseFloat(projectForm.gst_percentage),
        advance_amount: parseFloat(projectForm.advance_amount),
        site_visits_included: parseInt(projectForm.site_visits_included)
      };
      
      const response = await apiClient.post("/projects", data);
      toast.success("Project created successfully!");
      
      // Send email with attachment if requested
      if (emailForm.send_email && emailConfigured) {
        // Send email with optional PDF attachment
        const emailData = {
          recipient_email: client.email,
          recipient_name: client.name,
          subject: emailForm.subject || `Scope of Work - ${data.name}`,
          template_type: "scope_of_work",
          project_id: response.data.project_id,
          custom_message: emailForm.custom_message
        };
        
        if (emailForm.attachment) {
          // Upload with attachment
          const formData = new FormData();
          formData.append("file", emailForm.attachment);
          formData.append("recipient_email", client.email);
          formData.append("recipient_name", client.name);
          formData.append("subject", emailForm.subject || `Scope of Work - ${data.name}`);
          formData.append("project_id", response.data.project_id);
          formData.append("custom_message", emailForm.custom_message || "");
          
          await apiClient.post("/email/send-with-attachment", formData, {
            headers: { "Content-Type": "multipart/form-data" }
          });
          toast.success("Email sent with PDF attachment!");
        } else {
          await apiClient.post("/email/send", emailData);
          toast.success("Email sent to client!");
        }
      }
      
      setShowProjectModal(false);
      setEmailForm({ subject: "Scope of Work - ", custom_message: "", send_email: false, attachment: null });
      fetchClient();
      fetchClientProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create project");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendEmail = async () => {
    setSubmitting(true);
    try {
      await apiClient.post("/email/send", {
        recipient_email: client.email,
        recipient_name: client.name,
        subject: emailForm.subject,
        template_type: "scope_of_work",
        project_id: emailForm.project_id,
        custom_message: emailForm.custom_message
      });
      toast.success("Email sent successfully!");
      setShowEmailModal(false);
    } catch (error) {
      toast.error("Failed to send email");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this client? This action cannot be undone.")) return;
    
    try {
      await apiClient.delete(`/clients/${clientId}`);
      toast.success("Client deleted successfully");
      navigate("/clients");
    } catch (error) {
      toast.error("Failed to delete client");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const styles = {
      enquiry: "status-enquiry",
      proposal_sent: "status-pending",
      accepted: "status-active",
      active: "status-active",
      completed: "status-completed"
    };
    return `badge status-badge ${styles[status] || "bg-gray-700 text-gray-300"}`;
  };

  const amounts = calculateAmounts();

  if (loading) {
    return (
      <div className="page-content">
        <div className="space-y-6">
          <div className="h-8 skeleton w-1/4"></div>
          <div className="h-64 skeleton"></div>
        </div>
      </div>
    );
  }

  if (!client) return null;

  return (
    <div className="page-content animate-fade-in" data-testid="client-detail">
      {/* Header */}
      <div className="mb-6">
        <Link to="/clients" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-4 transition-colors group">
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Back to Clients
        </Link>
        
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4 animate-slide-in-left">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#FF4500] to-[#E65100] flex items-center justify-center shadow-lg animate-float">
              <span className="text-2xl font-bold text-white">{client.name?.charAt(0).toUpperCase()}</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">{client.name}</h1>
              <p className="text-gray-400">{client.project_name}</p>
              <div className="flex gap-2 mt-2">
                <span className={getStatusBadge(client.status)}>{client.status?.replace("_", " ")}</span>
                <span className={`badge type-${client.project_type}`}>{client.project_type}</span>
              </div>
            </div>
          </div>
          
          <div className="flex gap-2 animate-slide-in-right">
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/download/client/${clientId}/pdf`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                  });
                  const blob = await response.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `Client_${client.name.replace(/ /g, '_')}_Report.pdf`;
                  a.click();
                  toast.success("PDF downloaded!");
                } catch (err) {
                  toast.error("Failed to download PDF");
                }
              }}
              className="border-[#262626] hover:bg-[#262626] interactive"
              data-testid="download-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              PDF
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowEditModal(true)}
              className="border-[#262626] hover:bg-[#262626] interactive"
              data-testid="edit-client-btn"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
            <Button
              variant="outline"
              onClick={handleDelete}
              className="border-red-900 text-red-400 hover:bg-red-900/20 interactive"
              data-testid="delete-client-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Left Column - Client Info */}
        <div className="col-span-12 lg:col-span-4 space-y-6">
          {/* Contact Info */}
          <div className="bg-[#1A1A1A] border border-[#262626] rounded p-6 card-hover animate-fade-in-up stagger-1">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Contact Information</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3 group">
                <div className="w-8 h-8 rounded bg-[#FF4500]/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Mail className="w-4 h-4 text-[#FF4500]" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <a href={`mailto:${client.email}`} className="text-white hover:text-[#FF4500] transition-colors">
                    {client.email}
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-3 group">
                <div className="w-8 h-8 rounded bg-[#FF4500]/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Phone className="w-4 h-4 text-[#FF4500]" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Phone</p>
                  <a href={`tel:${client.phone}`} className="text-white hover:text-[#FF4500] transition-colors">
                    {client.phone}
                  </a>
                </div>
              </div>
              {client.address && (
                <div className="flex items-center gap-3 group">
                  <div className="w-8 h-8 rounded bg-[#FF4500]/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Building2 className="w-4 h-4 text-[#FF4500]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Address</p>
                    <p className="text-white">{client.address}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Project Info */}
          <div className="bg-[#1A1A1A] border border-[#262626] rounded p-6 card-hover animate-fade-in-up stagger-2">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Project Details</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-[#14B8A6]" />
                <div>
                  <p className="text-xs text-gray-500">Site Location</p>
                  <p className="text-white">{client.site_location}</p>
                </div>
              </div>
              {client.plot_area && (
                <div>
                  <p className="text-xs text-gray-500">Plot Area</p>
                  <p className="text-white">{client.plot_area}</p>
                </div>
              )}
              {client.approximate_sqft && (
                <div>
                  <p className="text-xs text-gray-500">Approximate Sqft</p>
                  <p className="text-white">{client.approximate_sqft} sqft</p>
                </div>
              )}
            </div>
          </div>

          {/* Contract Summary - Financial Tracking */}
          <div className="bg-[#1A1A1A] border border-[#262626] rounded p-6 card-hover animate-fade-in-up stagger-3">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Contract Summary</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFormData({...client});
                  setShowEditModal(true);
                }}
                className="text-xs text-gray-400 hover:text-white"
              >
                <Edit className="w-3 h-3 mr-1" /> Edit
              </Button>
            </div>
            <div className="space-y-4">
              {/* Fixed Rate Per Sqft */}
              <div className="flex items-center justify-between p-3 bg-[#171717] rounded border border-[#262626]">
                <div className="flex items-center gap-2">
                  <IndianRupee className="w-4 h-4 text-[#FF4500]" />
                  <span className="text-gray-400 text-sm">Fixed Rate/Sqft</span>
                </div>
                <span className="text-white font-semibold">
                  {client.rate_per_sqft ? formatCurrency(client.rate_per_sqft) : "Not set"}
                </span>
              </div>
              
              {/* Contract Amount */}
              <div className="flex items-center justify-between p-3 bg-[#171717] rounded border border-[#262626]">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#3B82F6]" />
                  <span className="text-gray-400 text-sm">Contract Amount</span>
                </div>
                <span className="text-white font-semibold">
                  {client.contract_amount ? formatCurrency(client.contract_amount) : "Not set"}
                </span>
              </div>
              
              {/* Total Received */}
              <div className="flex items-center justify-between p-3 bg-[#171717] rounded border-l-2 border-[#10B981]">
                <div className="flex items-center gap-2">
                  <IndianRupee className="w-4 h-4 text-[#10B981]" />
                  <span className="text-gray-400 text-sm">Total Received</span>
                </div>
                <span className="text-[#10B981] font-bold text-lg">
                  {formatCurrency(client.total_received || 0)}
                </span>
              </div>
              
              {/* Balance */}
              {client.contract_amount > 0 && (
                <div className="flex items-center justify-between p-3 bg-[#171717] rounded border-l-2 border-[#F59E0B]">
                  <div className="flex items-center gap-2">
                    <Calculator className="w-4 h-4 text-[#F59E0B]" />
                    <span className="text-gray-400 text-sm">Balance Pending</span>
                  </div>
                  <span className="text-[#F59E0B] font-bold text-lg">
                    {formatCurrency((client.contract_amount || 0) - (client.total_received || 0))}
                  </span>
                </div>
              )}
              
              {/* GST Info */}
              {client.gst_percentage > 0 && (
                <div className="text-xs text-gray-500 text-center pt-2 border-t border-[#262626]">
                  GST {client.gst_percentage}% extra on above amounts
                </div>
              )}
            </div>
          </div>

          {/* Status Actions */}
          <div className="bg-[#1A1A1A] border border-[#262626] rounded p-6 card-hover animate-fade-in-up stagger-3">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Update Status</h3>
            <Select value={client.status} onValueChange={handleStatusChange}>
              <SelectTrigger className="bg-[#171717] border-[#262626]" data-testid="status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#262626]">
                <SelectItem value="enquiry">Enquiry</SelectItem>
                <SelectItem value="proposal_sent">Proposal Sent</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          {client.notes && (
            <div className="bg-[#1A1A1A] border border-[#262626] rounded p-6 card-hover animate-fade-in-up stagger-4">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Notes</h3>
              <p className="text-gray-300 text-sm whitespace-pre-wrap">{client.notes}</p>
            </div>
          )}
        </div>

        {/* Right Column - Projects */}
        <div className="col-span-12 lg:col-span-8">
          <div className="bg-[#1A1A1A] border border-[#262626] rounded p-6 animate-fade-in-up stagger-2">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <FolderKanban className="w-5 h-5 text-[#FF4500]" />
                Projects
              </h3>
              <Button
                onClick={() => {
                  setProjectForm({
                    name: client.project_name,
                    project_type: client.project_type,
                    site_location: client.site_location,
                    approximate_sqft: client.approximate_sqft || "",
                    rate_per_sqft: client.rate_per_sqft || "",
                    discount_amount: "0",
                    final_agreed_amount: "",
                    gst_percentage: "18",
                    advance_amount: "40000",
                    site_visits_included: "10"
                  });
                  setEmailForm({ subject: `Scope of Work - ${client.project_name}`, custom_message: "", send_email: false });
                  setShowProjectModal(true);
                }}
                className="bg-[#FF4500] hover:bg-[#E65100] btn-glow"
                data-testid="create-project-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Project
              </Button>
            </div>

            {projects.length > 0 ? (
              <div className="space-y-4">
                {projects.map((project, idx) => (
                  <Link
                    key={project.project_id}
                    to={`/projects/${project.project_id}`}
                    className={`block p-4 bg-[#171717] rounded border border-[#262626] hover:border-[#FF4500]/50 transition-all glow-hover animate-fade-in stagger-${idx + 1}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-white">{project.name}</h4>
                      <span className={`badge ${project.status === 'active' ? 'status-active' : project.status === 'completed' ? 'status-completed' : 'status-pending'}`}>
                        {project.status}
                      </span>
                    </div>
                    
                    {/* Stage Progress Preview */}
                    <div className="flex gap-1 mb-3">
                      {project.stages?.slice(0, 6).map((stage, idx) => (
                        <div
                          key={idx}
                          className={`flex-1 h-2 rounded-full transition-all ${
                            stage.status === 'completed' ? 'bg-[#10B981]' :
                            stage.status === 'in_progress' ? 'bg-[#FF4500]' :
                            'bg-[#262626]'
                          }`}
                        />
                      ))}
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500 text-xs">Agreed Amount</p>
                        <p className="text-white font-medium">{formatCurrency(project.final_agreed_amount)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Rate/Sqft</p>
                        <p className="text-white font-medium">{formatCurrency(project.rate_per_sqft)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Site Visits</p>
                        <p className="text-white font-medium">{project.site_visits_used || 0}/{project.site_visits_included}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 animate-fade-in">
                <div className="w-16 h-16 rounded-full bg-[#262626] flex items-center justify-center mx-auto mb-4">
                  <FolderKanban className="w-8 h-8 text-gray-600" />
                </div>
                <h4 className="text-lg font-medium text-white mb-2">No projects yet</h4>
                <p className="text-gray-500 mb-4">Create a project to start tracking progress and payments.</p>
                <Button
                  onClick={() => setShowProjectModal(true)}
                  className="bg-[#FF4500] hover:bg-[#E65100] btn-glow"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Project
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <EditClientModal
        open={showEditModal}
        onOpenChange={setShowEditModal}
        formData={formData}
        setFormData={setFormData}
        newAdditionalEmail={newAdditionalEmail}
        setNewAdditionalEmail={setNewAdditionalEmail}
        handleUpdate={handleUpdate}
        submitting={submitting}
      />

      <CreateProjectModal
        open={showProjectModal}
        onOpenChange={setShowProjectModal}
        client={client}
        projectForm={projectForm}
        setProjectForm={setProjectForm}
        amounts={amounts}
        emailConfigured={emailConfigured}
        emailForm={emailForm}
        setEmailForm={setEmailForm}
        handleCreateProject={handleCreateProject}
        submitting={submitting}
      />

      <EmailConfirmationModal
        open={showEmailModal}
        onOpenChange={setShowEmailModal}
        client={client}
        emailForm={emailForm}
        setEmailForm={setEmailForm}
        handleSendEmail={handleSendEmail}
        submitting={submitting}
      />
    </div>
  );
}
